import Hls from 'hls.js';
import { getVideoOptions } from './config';

let hlsInstance: Hls | undefined = undefined;

export function loadRecording(videoId: string) {
  destroyHls();

  const recordingVideo = document.getElementById('recordingDetailVideo') as HTMLVideoElement;

  if (!videoId) {
    console.error('videoId does not exist, invalid playlist.m3u8 URL');
    return;
  }

  const version = Math.random().toString().substring(2);
  const hlsSrc = `${getVideoOptions().VIDEO_RECORDINGS_ROOT_URL}/${videoId}/playlist.m3u8?v=${version}`;

  if (Hls.isSupported()) {
    hlsInstance = new Hls();
    hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal && data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        console.error(`[RecordingDetailPlayback] Fatal HLS network error: ${data.details}`, data);
        hlsInstance?.destroy();
        hlsInstance = undefined;
        recordingVideo.style.display = 'none';
        const errorContainer = document.getElementById('recordingUnavailable');
        if (errorContainer) {
          errorContainer.style.display = '';
        }
      }
    });
    hlsInstance.loadSource(hlsSrc);
    hlsInstance.attachMedia(recordingVideo);
  } else if (recordingVideo.canPlayType('application/vnd.apple.mpegurl')) {
    recordingVideo.src = hlsSrc;
  }
}

export function destroyHls() {
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = undefined;
  }
}

type DownloadProgress = {
  status?: string;
  segment?: number;
  total?: number;
  percentage?: number;
  downloaded?: string;
};

type DownloadResult = {
  status: string;
  data: Blob;
};

type DownloadCallbacks = {
  progress: ((progress: DownloadProgress) => void) | null;
  finished: ((result: DownloadResult) => void) | null;
  error: ((error: string) => void) | null;
  aborted: (() => void) | null;
};

type DownloadHandle = {
  on(event: 'progress', cb: (progress: DownloadProgress) => void): DownloadHandle;
  on(event: 'finished', cb: (result: DownloadResult) => void): DownloadHandle;
  on(event: 'error', cb: (error: string) => void): DownloadHandle;
  on(event: 'aborted', cb: () => void): DownloadHandle;
  abort(): void;
};

export async function downloadRecording(
  videoId: string,
  filename: string,
  onProgress?: (percentage: number) => void,
): Promise<void> {
  if (!videoId) {
    throw new Error('videoId is required');
  }

  if (!filename) {
    throw new Error('filename is required');
  }

  const isIos = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
  if (isIos) {
    throw new Error('Downloading on iOS is not supported.');
  }

  const version = Math.random().toString().substring(2);
  const playlistUrl = `${getVideoOptions().VIDEO_RECORDINGS_ROOT_URL}/${videoId}/playlist.m3u8?v=${version}`;

  const m3u8 = new M3U8Downloader();

  await new Promise<void>((resolve, reject) => {
    m3u8
      .start(playlistUrl, { filename })
      .on('progress', (progress) => {
        if (onProgress && progress.percentage !== undefined) {
          onProgress(progress.percentage);
        }
      })
      .on('finished', () => {
        onProgress?.(100);
        resolve();
      })
      .on('error', (error: string) => {
        console.error('[Download] Error:', error);
        reject(new Error(error));
      })
      .on('aborted', () => {
        reject(new Error('Download aborted'));
      });
  });
}

class M3U8Downloader {
  private ie: boolean;
  private ios: boolean;

  constructor() {
    this.ie = navigator.appVersion.toString().indexOf('.NET') > 0;
    this.ios = !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
  }

  start(m3u8Url: string, options?: { filename?: string; returnBlob?: boolean }): DownloadHandle {
    if (!options) {
      options = {};
    }

    const callbacks: DownloadCallbacks = {
      progress: null,
      finished: null,
      error: null,
      aborted: null,
    };

    let recur: RecurseDownload | undefined;

    const handleCb = (key: keyof DownloadCallbacks, payload?: unknown) => {
      const callback = callbacks[key];
      if (callback) {
        (callback as (payload?: unknown) => void)(payload);
      }
    };

    if (this.ios) {
      handleCb('error', 'Downloading on iOS is not supported.');
    }

    const startObj = {
      on: (str: string, cb: unknown) => {
        switch (str) {
          case 'progress':
            callbacks.progress = cb as DownloadCallbacks['progress'];
            break;
          case 'finished':
            callbacks.finished = cb as DownloadCallbacks['finished'];
            break;
          case 'error':
            callbacks.error = cb as DownloadCallbacks['error'];
            break;
          case 'aborted':
            callbacks.aborted = cb as DownloadCallbacks['aborted'];
            break;
        }
        return startObj;
      },
      abort: () => {
        if (recur) {
          recur.aborted = () => {
            handleCb('aborted');
          };
        }
      },
    } as DownloadHandle;

    fetch(m3u8Url)
      .then((response) => response.text())
      .then((playlistText) => {
        const segmentReg = /^(?!#)(.+)\.(.+)$/gm;
        const segments = playlistText.match(segmentReg);

        if (!segments || segments.length === 0) {
          handleCb('error', 'Invalid m3u8 playlist');
          return;
        }

        const url = new URL(m3u8Url);
        const mapped = segments.map((v) => {
          const tempUrl = new URL(v, url);
          return tempUrl.href;
        });

        recur = new RecurseDownload(
          mapped,
          (data: ArrayBuffer[]) => {
            const blob = new Blob(data, { type: 'video/mp4' });

            handleCb('progress', { status: 'Processing...' });

            if (!options.returnBlob) {
              if (this.ie) {
                handleCb('progress', {
                  status: 'Sending video to Internet Explorer...',
                });
                (window.navigator as unknown as { msSaveBlob(blob: Blob, filename: string): void }).msSaveBlob(
                  blob,
                  options.filename || 'video.mp4',
                );
              } else {
                handleCb('progress', { status: 'Sending video to browser...' });

                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = options.filename || 'video.mp4';
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                setTimeout(() => URL.revokeObjectURL(a.href), 100);

                handleCb('finished', {
                  status: 'Successfully downloaded video',
                  data: blob,
                });
              }
            } else {
              handleCb('finished', {
                status: 'Successfully downloaded video',
                data: blob,
              });
            }
          },
          this.ie,
        );

        recur.onprogress = (obj) => {
          handleCb('progress', obj);
        };

        recur.onerror = (error: string) => {
          handleCb('error', error);
        };
      })
      .catch((err) => {
        handleCb('error', 'Something went wrong when downloading m3u8 playlist: ' + err);
      });

    return startObj;
  }
}

class RecurseDownload {
  public aborted: (() => void) | boolean = false;
  public onprogress?: (obj: DownloadProgress) => void;
  public onerror?: (error: string) => void;

  constructor(
    arr: string[],
    cb: (data: ArrayBuffer[]) => void,
    private ie: boolean,
  ) {
    this.recurseDownload(arr, cb, 0, []);
  }

  private recurseDownload(arr: string[], cb: (data: ArrayBuffer[]) => void, i: number, data: ArrayBuffer[]) {
    const current = arr[i];
    if (current === undefined) {
      cb(data);
      return;
    }

    const next = arr[i + 1];

    Promise.all([fetch(current), next ? fetch(next) : Promise.resolve(null)])
      .then((responses) => {
        return responses.filter((r): r is Response => r !== null && !!r.blob).map((r) => r.blob());
      })
      .then((blobPromises) => Promise.all(blobPromises))
      .then((blobs) => {
        const readers = blobs.map((blob, j) => {
          return new Promise<ArrayBuffer>((resolve) => {
            const reader = new FileReader();
            reader.addEventListener('loadend', () => {
              resolve(reader.result as ArrayBuffer);

              if (this.onprogress) {
                this.onprogress({
                  segment: i + j + 1,
                  total: arr.length,
                  percentage: Math.floor(((i + j + 1) / arr.length) * 100),
                  downloaded: this.formatNumber(data.reduce((sum, buf) => sum + buf.byteLength, 0)),
                  status: 'Downloading...',
                });
              }
            });
            reader.readAsArrayBuffer(blob);
          });
        });

        return Promise.all(readers);
      })
      .then((arrayBuffers) => {
        data.push(...arrayBuffers);

        const increment = arr[i + 2] ? 2 : 1;

        if (this.aborted) {
          if (typeof this.aborted === 'function') {
            this.aborted();
          }
          return;
        } else if (arr[i + increment]) {
          setTimeout(
            () => {
              this.recurseDownload(arr, cb, i + increment, data);
            },
            this.ie ? 500 : 0,
          );
        } else {
          cb(data);
        }
      })
      .catch((err) => {
        if (this.onerror) {
          this.onerror('Something went wrong when downloading ts file, nr. ' + i + ': ' + err);
        }
      });
  }

  private formatNumber(n: number): string {
    const ranges = [
      { divider: 1e18, suffix: 'EB' },
      { divider: 1e15, suffix: 'PB' },
      { divider: 1e12, suffix: 'TB' },
      { divider: 1e9, suffix: 'GB' },
      { divider: 1e6, suffix: 'MB' },
      { divider: 1e3, suffix: 'kB' },
    ];

    for (const range of ranges) {
      if (n >= range.divider) {
        const res = (n / range.divider).toString();
        return (res.split('.')[0] ?? res) + range.suffix;
      }
    }
    return n.toString();
  }
}
